/**
 * Short-lived access token, held in memory only.
 *
 * The long-lived credential is the httpOnly refresh cookie the API sets on
 * /api/v1/auth; JavaScript never sees it. This module trades that cookie for a
 * fresh access JWT whenever the current one is missing or about to expire.
 * Keeping the access token out of localStorage means an XSS can't walk away
 * with a credential that outlives the page.
 */

import { loadAuthData, saveAuthData } from './sessionStorage';
import type { Account, User } from './authTypes';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const REFRESH_URL = `${API_BASE_URL}/api/v1/auth/refresh`;
/** Refresh this long before the access token actually expires. */
const EXPIRY_MARGIN_MS = 60_000;

export type RefreshedSession = {
  token: string;
  supabaseToken: string | null;
  session: { account: Account; user: User };
};

export type RefreshOutcome =
  | { status: 'ok'; data: RefreshedSession }
  | { status: 'signed_out' }
  | { status: 'network_error' };

let accessToken: string | null = null;
let accessTokenExpiresAt = 0;
let inFlight: Promise<RefreshOutcome> | null = null;
const refreshListeners = new Set<(outcome: RefreshOutcome) => void>();

function decodeExpiry(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
  } catch {
    return 0;
  }
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  accessTokenExpiresAt = token ? decodeExpiry(token) : 0;
}

/** The current access token, or null. Synchronous; may be close to expiry. */
export function getAccessToken(): string | null {
  return accessToken;
}

export function accessTokenExpiresAtMs(): number {
  return accessTokenExpiresAt;
}

/** Every refresh outcome, from any caller. AuthContext uses this to stay in sync. */
export function onRefresh(listener: (outcome: RefreshOutcome) => void): () => void {
  refreshListeners.add(listener);
  return () => refreshListeners.delete(listener);
}

/**
 * Exchange the refresh cookie for a new access token. Concurrent callers in
 * this tab share one request; across tabs, a Web Lock serialises refreshes so
 * two tabs never present the same refresh token (the server would see reuse).
 */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  if (!inFlight) {
    inFlight = withRefreshLock(doRefresh).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** A usable access token, refreshing first if it is missing or nearly expired. */
export async function ensureFreshAccessToken(): Promise<string | null> {
  if (accessToken && Date.now() < accessTokenExpiresAt - EXPIRY_MARGIN_MS) {
    return accessToken;
  }
  const outcome = await refreshAccessToken();
  return outcome.status === 'ok' ? outcome.data.token : null;
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request('littlebooker-auth-refresh', fn);
}

async function doRefresh(): Promise<RefreshOutcome> {
  // Transitional: sessions created before refresh tokens existed only have the
  // old 7-day JWT in localStorage. The server swaps it for a refresh cookie once.
  const legacyToken = loadAuthData()?.token;

  let outcome: RefreshOutcome;
  try {
    const response = await fetch(REFRESH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(legacyToken ? { legacyToken } : {}),
    });

    if (response.status === 401 || response.status === 403) {
      setAccessToken(null);
      outcome = { status: 'signed_out' };
    } else if (!response.ok) {
      outcome = { status: 'network_error' };
    } else {
      const data = (await response.json()) as RefreshedSession;
      setAccessToken(data.token);
      outcome = { status: 'ok', data };
    }
  } catch {
    outcome = { status: 'network_error' };
  }

  if (legacyToken && outcome.status !== 'network_error') {
    // Either upgraded to a cookie session or rejected: the legacy token is done.
    const stored = loadAuthData();
    if (stored) {
      const { token: _legacy, ...rest } = stored;
      saveAuthData(rest);
    }
  }

  refreshListeners.forEach((listener) => listener(outcome));
  return outcome;
}
